import type {
  CandidateFreshness,
  CandidateSummary,
  CandidateSupersessionReason,
} from "@/api/preview";

/**
 * The server's currency reading of a frozen candidate, in a clinician's words.
 *
 * `CandidateSummary.freshness` and `LifecycleCandidate.freshness` say whether
 * the immutable candidate still represents the Project's CURRENT effective
 * configuration — the draft version, the adoption pin-set digest and the base
 * identity it froze, compared server-side against the Project's current rows,
 * and whether a later freeze exists. This module only turns that reading into
 * sentences. Nothing here re-derives it: not from `validity`, not from list
 * position, not from the freeze instant — the inference that let the Preview
 * modal launch `Testing version 1` as current after its BG sets were removed.
 */

/**
 * Whether the server says this candidate IS the current configuration.
 *
 * FAIL CLOSED on an absent reading: a cached snapshot from before the contract
 * carried `freshness` says nothing about currency, and treating silence as
 * "current" is exactly the defect. The service holds the freeze gate, so the
 * worst outcome of a false negative is one redundant freeze of unchanged
 * content — byte-identical by construction (#16's reproducibility rule).
 */
export function isCurrent(candidate: CandidateSummary): boolean {
  return candidate.freshness?.state === "current";
}

/**
 * What moved, per reason, as the fragment that completes "frozen from a
 * previous configuration: …". `satisfies` so a member added to the contract
 * fails the build here rather than rendering blank (the enum-change checklist
 * in contracts/README.md); the fallback clause below covers a member a
 * deployed bundle has no copy for yet, which is still a supersession.
 */
const REASON_COPY = {
  newer_candidate: "a newer test version has been frozen since",
  draft_changed: "the draft changed after it was frozen",
  adoptions_changed:
    "the adopted library content changed after it was frozen",
  governed_settings_changed:
    "a project setting changed after it was frozen",
  base_changed: "the base pack changed after it was frozen",
  final_questioning_changed:
    "the final safety questioning setting changed after it was frozen",
} satisfies Record<CandidateSupersessionReason, string>;

const UNKNOWN_REASON = "the configuration changed after it was frozen";

/**
 * One sentence stating why the candidate is no longer current, or null for a
 * current one. The sentence names every reason the server gave, joined the
 * way a clinician would say it, and ends with the recovery — a NEW freeze —
 * because the old version is never renewed, relabelled or deleted.
 */
export function supersessionSentence(
  freshness: CandidateFreshness | null | undefined,
): string | null {
  if (freshness === null || freshness === undefined) {
    // No reading at all: not provably current, and not something this
    // module may explain — the launch control's copy covers the recovery.
    return null;
  }
  if (freshness.state !== "superseded") {
    return null;
  }
  const fragments = freshness.reasons.map(
    (reason) =>
      (REASON_COPY as Record<string, string>)[reason] ?? UNKNOWN_REASON,
  );
  const unique = [...new Set(fragments)];
  const joined =
    unique.length <= 1
      ? (unique[0] ?? UNKNOWN_REASON)
      : `${unique.slice(0, -1).join(", ")} and ${unique[unique.length - 1]}`;
  return `This test version was frozen from a previous configuration: ${joined}. Launching freezes a new test version of the current configuration.`;
}
